import { useState, useCallback, useEffect, useRef } from "react";
import { Tabs, TabList, Tab, TabPanels, TabPanel } from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import {
	useReviewClarifications,
	useReviewSummary,
	useTriageClarification,
	useBatchTriage,
	useLinkDuplicates,
	useEditClarificationText,
	useApplyToAll,
	useCompleteReviewStep,
	useAIReview,
} from "../../../hooks/useClarificationReview";
import { useHasAIAccess } from "hooks/featureFlags";
import { ClarificationsRegister } from "newui/features/Rfx/Clarifications";
import type {
	ReviewClarification,
	TriageStatus,
	DeclineReason,
} from "../../../types/clarification-review.type";
import type { PanelMode } from "../../../types/panel.type";
import { ClarificationReviewTable } from "./components/ClarificationReviewTable";
import { ReviewProgressBar } from "./components/ReviewProgressBar";
import { BatchActionsBar } from "./components/BatchActionsBar";
import { AIReviewPanel } from "./components/AIReviewPanel";
import { DuplicateSelectionModal } from "./components/DuplicateSelectionModal";
import { EditClarificationModal } from "./components/EditClarificationModal";
import { ApplyToAllModal } from "./components/ApplyToAllModal";
import { StepCompleteModal } from "./components/StepCompleteModal";

interface ClarificationsStepProps {
	rfxId: number;
	rfxSubmissionId?: number;
	isLoading?: boolean;
	isRfxLead?: boolean;
	panelMode?: PanelMode;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
}

const isPanelModeType = (mode?: PanelMode): boolean => {
	return mode === "manual_panel" || mode === "ai_panel";
};

export const ClarificationsStep: React.FC<ClarificationsStepProps> = ({
	rfxId,
	rfxSubmissionId,
	isLoading = false,
	isRfxLead = false,
	panelMode,
	onBeforeNext,
}) => {
	const { data: clarifications, isLoading: isLoadingClarifications, error } = useReviewClarifications(rfxId);
	const summary = useReviewSummary(clarifications);
	const triageMutation = useTriageClarification(rfxId);
	const batchMutation = useBatchTriage(rfxId);
	const linkDuplicatesMutation = useLinkDuplicates(rfxId);
	const editTextMutation = useEditClarificationText(rfxId);
	const applyToAllMutation = useApplyToAll(rfxId);
	const completeStepMutation = useCompleteReviewStep(rfxId);
	const aiReviewMutation = useAIReview(rfxId);
	const hasAIAccess = useHasAIAccess();
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [showDuplicateModal, setShowDuplicateModal] = useState(false);
	const [duplicateCandidates, setDuplicateCandidates] = useState<ReviewClarification[]>([]);
	const [editingClarification, setEditingClarification] = useState<ReviewClarification | null>(null);
	const [applyToAllClarification, setApplyToAllClarification] = useState<ReviewClarification | null>(null);
	const [showCompleteModal, setShowCompleteModal] = useState(false);
	const completeResolveRef = useRef<((proceed: boolean) => void) | null>(null);

	const items = clarifications ?? [];
	const isPanel = isPanelModeType(panelMode);

	const handleTriage = useCallback(
		(clarificationId: number, status: TriageStatus, declineReason?: string, declineCategory?: DeclineReason) => {
			triageMutation.mutate({
				clarificationId,
				payload: {
					triage_status: status,
					decline_reason: declineReason,
					decline_reason_category: declineCategory,
				},
			});
		},
		[triageMutation]
	);

	const handleMarkDuplicate = useCallback(
		(clarificationId: number) => {
			// Add this clarification to selected if not already
			const idsToUse = selectedIds.includes(clarificationId)
				? selectedIds
				: [...selectedIds, clarificationId];

			if (idsToUse.length < 2) {
				// Need at least 2 — select this one and prompt user to select more
				setSelectedIds(idsToUse);
				return;
			}

			const candidates = items.filter((c) => idsToUse.includes(c.id));
			setDuplicateCandidates(candidates);
			setShowDuplicateModal(true);
		},
		[selectedIds, items]
	);

	const handleLinkDuplicatesFromBatch = useCallback(() => {
		if (selectedIds.length < 2) {
			return;
		}
		const candidates = items.filter((c) => selectedIds.includes(c.id));
		setDuplicateCandidates(candidates);
		setShowDuplicateModal(true);
	}, [selectedIds, items]);

	const handleConfirmDuplicates = useCallback(
		(mainId: number, duplicateIds: number[]) => {
			linkDuplicatesMutation.mutate(
				{
					clarificationId: mainId,
					payload: {
						main_clarification_id: mainId,
						duplicate_ids: duplicateIds,
					},
				},
				{
					onSuccess: () => {
						setShowDuplicateModal(false);
						setDuplicateCandidates([]);
						setSelectedIds([]);
					},
				}
			);
		},
		[linkDuplicatesMutation]
	);

	const handleBatchAccept = useCallback(() => {
		if (selectedIds.length === 0) {
			return;
		}
		batchMutation.mutate(
			{
				clarification_ids: selectedIds,
				triage_status: "accepted",
			},
			{
				onSuccess: () => {
					setSelectedIds([]);
				},
			}
		);
	}, [selectedIds, batchMutation]);

	const handleRowClick = useCallback((clarification: ReviewClarification) => {
		setEditingClarification(clarification);
	}, []);

	const handleEditSave = useCallback(
		(clarificationId: number, text: string) => {
			editTextMutation.mutate(
				{
					clarificationId,
					payload: { clarification_text: text },
				},
				{
					onSuccess: () => {
						setEditingClarification(null);
					},
				}
			);
		},
		[editTextMutation]
	);

	const uniqueSupplierCount = new Set(items.map((c) => c.rfx_invite_id)).size;

	const handleApplyToAll = useCallback(
		(clarificationId: number) => {
			const clarification = items.find((c) => c.id === clarificationId);
			if (clarification) {
				setApplyToAllClarification(clarification);
			}
		},
		[items]
	);

	const handleConfirmApplyToAll = useCallback(
		(clarificationId: number) => {
			applyToAllMutation.mutate(
				{
					clarificationId,
					payload: { clarification_id: clarificationId },
				},
				{
					onSuccess: () => {
						setApplyToAllClarification(null);
					},
				}
			);
		},
		[applyToAllMutation]
	);

	const handleBatchDecline = useCallback(
		(reason: string, category: DeclineReason) => {
			if (selectedIds.length === 0) {
				return;
			}
			batchMutation.mutate(
				{
					clarification_ids: selectedIds,
					triage_status: "declined",
					decline_reason: reason,
					decline_reason_category: category,
				},
				{
					onSuccess: () => {
						setSelectedIds([]);
					},
				}
			);
		},
		[selectedIds, batchMutation]
	);

	const handleCompleteConfirm = useCallback((dateDue?: string) => {
		completeStepMutation.mutate(
			{ date_due: dateDue },
			{
				onSuccess: () => {
					setShowCompleteModal(false);
					if (completeResolveRef.current) {
						completeResolveRef.current(true);
						completeResolveRef.current = null;
					}
				},
				onError: () => {
					// Keep modal open on error — user can retry or cancel
				},
			}
		);
	}, [completeStepMutation]);

	const handleCompleteCancel = useCallback(() => {
		setShowCompleteModal(false);
		if (completeResolveRef.current) {
			completeResolveRef.current(false);
			completeResolveRef.current = null;
		}
	}, []);

	const handleRequestAIReview = useCallback(() => {
		aiReviewMutation.mutate();
	}, [aiReviewMutation]);

	const handleAcceptAISuggestion = useCallback(
		(clarificationId: number, status: TriageStatus, declineReason?: string, declineCategory?: DeclineReason) => {
			handleTriage(clarificationId, status, declineReason, declineCategory);
		},
		[handleTriage]
	);

	const handleAcceptAIDuplicateGroup = useCallback(
		(mainId: number, duplicateIds: number[]) => {
			linkDuplicatesMutation.mutate({
				clarificationId: mainId,
				payload: {
					main_clarification_id: mainId,
					duplicate_ids: duplicateIds,
				},
			});
		},
		[linkDuplicatesMutation]
	);

	useEffect(() => {
		if (onBeforeNext && isRfxLead) {
			onBeforeNext(() => {
				return new Promise<boolean>((resolve) => {
					completeResolveRef.current = resolve;
					setShowCompleteModal(true);
				});
			});
		}
	}, [onBeforeNext, isRfxLead]);

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	const reviewContent = (
		<>
			<p className="text-sm text-gray-500">
				Review all proposed clarifications from panel members. Accept, decline, or mark as duplicate before sending to suppliers.
			</p>

			<ReviewProgressBar summary={summary} />

			{isRfxLead && hasAIAccess && (
				<AIReviewPanel
					rfxId={rfxId}
					clarifications={items}
					aiReview={aiReviewMutation.data ?? null}
					isLoading={aiReviewMutation.isPending}
					onRequestReview={handleRequestAIReview}
					onAcceptSuggestion={handleAcceptAISuggestion}
					onAcceptDuplicateGroup={handleAcceptAIDuplicateGroup}
				/>
			)}

			{isRfxLead && (
				<BatchActionsBar
					selectedCount={selectedIds.length}
					onBatchAccept={handleBatchAccept}
					onBatchDecline={handleBatchDecline}
					onLinkDuplicates={handleLinkDuplicatesFromBatch}
					onClearSelection={() => setSelectedIds([])}
					isProcessing={batchMutation.isPending}
				/>
			)}

			<ClarificationReviewTable
				clarifications={items}
				isLoading={isLoadingClarifications}
				error={error}
				selectedIds={selectedIds}
				onSelectionChange={setSelectedIds}
				onTriage={handleTriage}
				onMarkDuplicate={handleMarkDuplicate}
				onApplyToAll={isRfxLead ? handleApplyToAll : undefined}
				onRowClick={handleRowClick}
				isTriaging={triageMutation.isPending}
				isReadOnly={!isRfxLead}
			/>
		</>
	);

	const leadModals = isRfxLead ? (
		<>
			<EditClarificationModal
				isOpen={editingClarification !== null}
				clarification={editingClarification}
				onClose={() => setEditingClarification(null)}
				onSave={handleEditSave}
				isLoading={editTextMutation.isPending}
			/>

			<ApplyToAllModal
				isOpen={applyToAllClarification !== null}
				clarification={applyToAllClarification}
				supplierCount={Math.max(0, uniqueSupplierCount - 1)}
				onClose={() => setApplyToAllClarification(null)}
				onConfirm={handleConfirmApplyToAll}
				isLoading={applyToAllMutation.isPending}
			/>

			<DuplicateSelectionModal
				isOpen={showDuplicateModal}
				clarifications={duplicateCandidates}
				onClose={() => {
					setShowDuplicateModal(false);
					setDuplicateCandidates([]);
				}}
				onConfirm={handleConfirmDuplicates}
				isLoading={linkDuplicatesMutation.isPending}
			/>

			<StepCompleteModal
				isOpen={showCompleteModal}
				summary={summary}
				onClose={handleCompleteCancel}
				onConfirm={handleCompleteConfirm}
				isLoading={completeStepMutation.isPending}
			/>
		</>
	) : null;

	// Panel mode + RFx Lead: tabbed layout with Review and Accepted Clarifications
	if (isRfxLead && isPanel) {
		return (
			<div data-testid="clarifications-step" className="space-y-4">
				<Tabs data-testid="clarifications-tabs" isLazy>
					<TabList>
						<Tab data-testid="tab-review">Review</Tab>
						<Tab data-testid="tab-accepted-clarifications">Accepted Clarifications</Tab>
					</TabList>
					<TabPanels>
						<TabPanel data-testid="tab-panel-review" className="space-y-4 px-0">
							{reviewContent}
						</TabPanel>
						<TabPanel data-testid="tab-panel-accepted-clarifications" className="px-0">
							<ClarificationsRegister
								rfxId={rfxId}
								isClientView={true}
								supplierName=""
							/>
						</TabPanel>
					</TabPanels>
				</Tabs>
				{leadModals}
			</div>
		);
	}

	// Solo mode + RFx Lead: ClarificationsRegister directly with CRUD
	if (isRfxLead && !isPanel) {
		return (
			<div data-testid="clarifications-step" className="space-y-4">
				<ClarificationsRegister
					rfxId={rfxId}
					isClientView={true}
					supplierName=""
				/>
				{leadModals}
			</div>
		);
	}

	// Non-lead users: read-only ClarificationsRegister
	return (
		<div data-testid="clarifications-step" className="space-y-4">
			<ClarificationsRegister
				rfxId={rfxId}
				isClientView={false}
				supplierName=""
			/>
		</div>
	);
};
